import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Guitar, Instagram, Youtube, Music, Globe, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { PublicHeader, PublicFooter } from '@/components/PublicShell';
import { BandStatusBadge, StageStatusBadge, formatDateShortPt } from '@/components/Badges';

export default function BandDetailPage() {
  const { slug } = useParams();
  const [band, setBand] = useState(null);
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/bands/${slug}`).then((r) => setBand(r.data)).catch(() => setError(true));
    api.get('/public/settings').then((r) => setSettings(r.data));
  }, [slug]);

  if (error) return <NotFound />;
  if (!band) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregando…</div>;

  return (
    <div>
      <PublicHeader />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
        <Link to="/bandas" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-white mb-4">
          <ArrowLeft className="h-4 w-4" /> Voltar para bandas
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <div className="rounded-xl overflow-hidden border border-border bg-card">
              <div className="aspect-square bg-gradient-to-br from-[#1a1e2c] to-[#0b0c0f] flex items-center justify-center">
                {band.logo_url ? <img src={band.logo_url} alt={band.name} className="w-full h-full object-cover" /> : <Guitar className="w-20 h-20 text-muted-foreground" />}
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <BandStatusBadge status={band.status} />
                  {band.city && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="w-3 h-3" />{band.city}{band.state ? `/${band.state}` : ''}</span>}
                </div>
                <div className="mt-3 space-y-2 text-sm">
                  {band.instagram_url && <SocialLink icon={Instagram} url={band.instagram_url} label="Instagram" />}
                  {band.youtube_url && <SocialLink icon={Youtube} url={band.youtube_url} label="YouTube" />}
                  {band.spotify_url && <SocialLink icon={Music} url={band.spotify_url} label="Spotify" />}
                  {band.website_url && <SocialLink icon={Globe} url={band.website_url} label="Site" />}
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-8 space-y-6">
            <div>
              <div className="font-display text-4xl sm:text-5xl text-white leading-none">{band.name}</div>
              {band.short_description && <p className="mt-2 text-muted-foreground">{band.short_description}</p>}
              {band.full_description && band.full_description !== band.short_description && (
                <p className="mt-4 text-sm text-white/85 whitespace-pre-line leading-relaxed">{band.full_description}</p>
              )}
            </div>

            {band.stats && (
              <div className="grid grid-cols-3 gap-3">
                <StatCard label="Vitórias" value={band.stats.wins} />
                <StatCard label="Derrotas" value={band.stats.losses} />
                <StatCard label="Etapas" value={band.stats.total} />
              </div>
            )}

            <Card className="border border-border bg-card">
              <CardContent className="p-5">
                <div className="font-display text-2xl text-white mb-3">HISTÓRICO</div>
                {band.history?.length ? (
                  <div className="space-y-2">
                    {band.history.map((h) => (
                      <div key={h.stage_id} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-[var(--g66-surface)] p-3">
                        <div className="min-w-0">
                          <div className="text-xs text-muted-foreground">{formatDateShortPt(h.date)}</div>
                          <Link to={`/etapas/${h.stage_slug}`} className="font-medium text-white hover:text-[var(--g66-amber)]">
                            {h.title}
                          </Link>
                          <div className="text-xs text-muted-foreground">vs {h.opponent?.name || '-'}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StageStatusBadge status={h.status} />
                          {h.status === 'finished' && (
                            <div className={`font-display text-xl ${h.result === 'vitoria' ? 'text-[var(--g66-amber)]' : 'text-[var(--g66-red)]'}`}>
                              {h.result === 'vitoria' ? 'V' : 'D'} · {h.my_percentage?.toFixed?.(0) || 0}%
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">Sem disputas registradas ainda.</div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <PublicFooter settings={settings} />
    </div>
  );
}

function NotFound() {
  return (
    <div>
      <PublicHeader />
      <div className="min-h-[50vh] flex items-center justify-center text-center px-4">
        <div>
          <div className="font-display text-4xl text-white">Banda não encontrada</div>
          <Link to="/bandas"><Button className="mt-4">Ver todas as bandas</Button></Link>
        </div>
      </div>
    </div>
  );
}

function SocialLink({ icon: Icon, url, label }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-muted-foreground hover:text-[var(--g66-amber)]">
      <Icon className="w-4 h-4" /> {label}
    </a>
  );
}

function StatCard({ label, value }) {
  return (
    <Card className="border border-border bg-card">
      <CardContent className="p-4 text-center">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
        <div className="font-display text-3xl text-white mt-1">{value ?? 0}</div>
      </CardContent>
    </Card>
  );
}
